import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import TicketTypeCard from "../tickets/TicketTypeCard";
import TicketTypeDrawer from "../tickets/TicketTypeDrawer";
import Dialog from "../../../../../dialog/Dialog";
import { useTicketTypeSummary } from "../../hooks/useTicketTypeSummary";
import { deleteActivityTicket } from "../../activityService";
import Toster from "../../../../../common/Toster";

const SectionTicketTypes = ({ activityId }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [drawerSessionKey, setDrawerSessionKey] = useState(0);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, ticket: null });
  const [showToster, setShowToster] = useState(false);
  const [tosterMessage, setTosterMessage] = useState("");
  const [tosterType, setTosterType] = useState("success");
  const queryClient = useQueryClient();
  const { summaries, isLoading, tickets } = useTicketTypeSummary(activityId);

  const deleteMutation = useMutation({
    mutationFn: (ticketId) => deleteActivityTicket(activityId, ticketId),
    onSuccess: (_data, ticketId) => {
      queryClient.invalidateQueries(["activityTickets", activityId]);
      queryClient.invalidateQueries(["activityTicketTypePrices", activityId]);
      queryClient.invalidateQueries(["activityInclusions", activityId, ticketId]);
      queryClient.invalidateQueries(["activityExclusions", activityId, ticketId]);
      queryClient.invalidateQueries(["activityItinerary", activityId, ticketId]);
      setDeleteModal({ isOpen: false, ticket: null });
      setTosterMessage("Ticket type deleted successfully");
      setTosterType("success");
      setShowToster(true);
    },
    onError: (error) => {
      setTosterMessage(
        error?.response?.data?.message || error?.message || "Failed to delete ticket type"
      );
      setTosterType("error");
      setShowToster(true);
    },
  });

  const openAdd = () => {
    setEditingTicket(null);
    setIsNew(true);
    setDrawerSessionKey((k) => k + 1);
    setDrawerOpen(true);
  };

  const openEdit = (summary) => {
    const full = tickets.find((t) => Number(t.id) === Number(summary.id)) || summary;
    setEditingTicket(full);
    setIsNew(false);
    setDrawerSessionKey((k) => k + 1);
    setDrawerOpen(true);
  };

  const handleDeleteRequest = (ticket) => {
    setDeleteModal({ isOpen: true, ticket });
  };

  const confirmDelete = () => {
    if (deleteModal.ticket?.id) {
      deleteMutation.mutate(deleteModal.ticket.id);
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setEditingTicket(null);
    setIsNew(false);
  };

  if (!activityId) {
    return <p className="text-sm text-gray-500">Save General Information first.</p>;
  }

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Ticket types are the core of your activity. Each ticket can have its own pricing, inclusions,
        exclusions, and itinerary.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {summaries.map((ticket) => (
          <TicketTypeCard
            key={ticket.id}
            ticket={ticket}
            onEdit={openEdit}
            onDelete={handleDeleteRequest}
          />
        ))}

        <button
          type="button"
          onClick={openAdd}
          className="flex min-h-[200px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50/50 p-5 text-gray-600 transition hover:border-primary hover:bg-primary/5 hover:text-primary"
        >
          <i className="fi fi-rr-plus mb-2 text-2xl" />
          <span className="text-sm font-semibold">Add ticket type</span>
        </button>
      </div>

      {tickets.length === 0 && (
        <p className="text-center text-sm text-amber-700">
          Add at least one ticket type to continue.
        </p>
      )}

      <TicketTypeDrawer
        key={drawerSessionKey}
        open={drawerOpen}
        onClose={handleDrawerClose}
        activityId={activityId}
        ticket={editingTicket}
        isNew={isNew}
      />

      <Dialog
        open={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, ticket: null })}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-trash text-base" />
            Delete ticket type
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={confirmDelete}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteModal({ isOpen: false, ticket: null })}
        isLoading={deleteMutation.isPending}
        maxWidth="md"
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete{" "}
            <strong>{deleteModal.ticket?.name || "this ticket type"}</strong>? This will
            also remove its pricing, inclusions, exclusions, and itinerary.
          </p>
        </div>
      </Dialog>

      {showToster && (
        <Toster
          message={tosterMessage}
          type={tosterType}
          onClose={() => setShowToster(false)}
        />
      )}
    </div>
  );
};

export default SectionTicketTypes;
